import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { getUserLocalePreference } from '../../common/users/utils/userPropertyUtils';
import { getIsFeatureEnabledForCurrentUser } from '../element/feature/elementFeatureSelector';
import { ExperimentId } from '../../common/experiments/experimentsConstants';
import { getCurrentUser, isUserAvailableSelector, isGuestSelector } from '../user/currentUserSelector';
import i18n, { switchToCdnBackend, isUsingCdn } from './i18n/initI18n';
import globalLogger from '../logger';
import { getAuthLocale } from '../auth/localisation/authLocaleService';
import { getSavedAuthLocale, saveAuthLocale } from '../auth/localisation/authLocaleStorage';

const logger = globalLogger.createChannel('i18n');

/**
 * Synchronizes the given locale preference to localStorage for auth pages.
 */
const syncAuthLocale = (localePreference: string | undefined | null): void => {
    saveAuthLocale(localePreference || i18n.language || null);
};

/**
 * Manager component that initializes i18n language based on user preferences
 * and switches between local and CDN backends based on feature flags.
 *
 * Initialization logic:
 * - Authenticated users: Uses their saved locale preference
 * - Guest/Logged out users: Falls back to saved auth locale or device/browser language
 * - Switches to CDN backend when the feature flag is enabled
 *
 * Should be mounted after user data is available.
 */
const I18nLanguageManager = () => {
    const currentUser = useSelector(getCurrentUser);
    const isUserAvailable = useSelector(isUserAvailableSelector);
    const isGuest = useSelector(isGuestSelector);
    const isFetchLocalesFromCDNEnabled = useSelector(
        getIsFeatureEnabledForCurrentUser(ExperimentId.fetchLocalesFromCDN),
    );
    const hasInitialized = useRef(false);

    useEffect(() => {
        // Only run initialization once when user is available
        if (!isUserAvailable || hasInitialized.current) {
            return;
        }

        const initializeI18n = async () => {
            // Switch to CDN backend if feature is enabled for fetching locales from CDN
            if (isFetchLocalesFromCDNEnabled && !isUsingCdn()) {
                try {
                    await switchToCdnBackend();
                } catch (error) {
                    logger.error('Failed to switch to CDN backend:', error);
                    // Continue with local backend if CDN switch fails
                }
            }

            // Determine and apply the appropriate locale
            const userLocalePreference = getUserLocalePreference(currentUser);

            if (userLocalePreference) {
                // Authenticated user with locale preference: use it
                syncAuthLocale(userLocalePreference);
                await i18n.changeLanguage(userLocalePreference);
            } else if (isGuest) {
                // Guest user: resolve locale from saved preference or device/browser
                const guestLocale = await getAuthLocale({
                    savedLocale: getSavedAuthLocale(),
                });

                if (i18n.language !== guestLocale) {
                    await i18n.changeLanguage(guestLocale);
                }
            } else {
                // Authenticated user without locale preference: sync current i18n language
                syncAuthLocale(i18n.language);
            }

            hasInitialized.current = true;
        };

        initializeI18n();
    }, [currentUser, isUserAvailable, isGuest, isFetchLocalesFromCDNEnabled]);

    // Handle language changes when user updates their preference
    useEffect(() => {
        if (!hasInitialized.current || !isUserAvailable) {
            return;
        }

        const userLocalePreference = getUserLocalePreference(currentUser);
        syncAuthLocale(userLocalePreference);

        if (userLocalePreference && i18n.language !== userLocalePreference) {
            i18n.changeLanguage(userLocalePreference);
        }
    }, [currentUser, isUserAvailable]);

    return null;
};

export default I18nLanguageManager;
